'use client';

import { useEffect, useState } from 'react';

export interface StoneGuideTocItem {
  id: string;
  heading: string;
}

export interface StoneGuideTOCProps {
  sections: StoneGuideTocItem[];
  readingTime: number;
  lastUpdated: string;
  locale: string;
  title?: string;
}

export function StoneGuideTOC({
  sections,
  readingTime,
  lastUpdated,
  locale,
  title,
}: StoneGuideTOCProps) {
  const [activeId, setActiveId] = useState(sections[0]?.id ?? '');

  const tocTitle =
    title ?? (locale === 'fr' ? 'Dans ce guide' : 'On This Page');
  const readingLabel = locale === 'fr' ? 'Lecture' : 'Reading time';
  const updatedLabel = locale === 'fr' ? 'Mis à jour' : 'Last updated';

  useEffect(() => {
    const handleScroll = () => {
      const offset = 120;
      const scrollPosition = window.scrollY + offset;

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]!.id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveId(sections[i]!.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const top = element.offsetTop - 100;
      window.scrollTo({ top, behavior: 'smooth' });
      setActiveId(id);
    }
  };

  const formattedDate = new Date(lastUpdated).toLocaleDateString(
    locale === 'fr' ? 'fr-FR' : 'en-GB',
    { day: 'numeric', month: 'long', year: 'numeric' },
  );

  if (sections.length === 0) return null;

  return (
    <aside className="sticky top-24">
      <div className="bg-white shadow-lg p-6">
        <h4 className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-4">
          {tocTitle}
        </h4>

        <ul className="space-y-1 mb-6">
          {sections.map((section) => {
            const isActive = section.id === activeId;

            return (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  onClick={(event) => handleClick(event, section.id)}
                  className={`
                    block py-2 pl-4 text-sm font-sans transition-colors border-l-2
                    ${
                      isActive
                        ? 'text-stone-900 border-stone-900 font-medium'
                        : 'text-stone-500 border-stone-300 hover:text-stone-700 hover:border-stone-400'
                    }
                  `}
                >
                  {section.heading}
                </a>
              </li>
            );
          })}
        </ul>

        <div className="pt-4 border-t border-stone-200 space-y-2">
          <p className="text-xs font-sans text-stone-500">
            <span className="uppercase tracking-[0.15em] text-[10px] text-stone-400">
              {readingLabel}
            </span>
            <span className="block text-stone-700 mt-1">
              {readingTime} {locale === 'fr' ? 'min' : 'min read'}
            </span>
          </p>
          <p className="text-xs font-sans text-stone-500">
            <span className="uppercase tracking-[0.15em] text-[10px] text-stone-400">
              {updatedLabel}
            </span>
            <span className="block text-stone-700 mt-1">{formattedDate}</span>
          </p>
        </div>
      </div>
    </aside>
  );
}

export default StoneGuideTOC;
